// The bridge between a `PanelResult` and what the chart primitives take.
//
// A panel describes itself: `groupBy` says what each `rows[].key` position means,
// `members` carries the display information for the ids that appear in those
// positions, and `measures` says how each number is formatted. Reading a panel
// therefore always starts the same way — find the dimension's position, index its
// members by id, then walk the rows. That preamble lives here once, because four
// panels each carrying their own copy of it is how the "a panel describes itself"
// property quietly turns back into four sets of assumptions.
//
// Nothing in this file knows which panel it is looking at. `top_items` and
// `by_type` differ in their dimension, not in how they are read.

import { TIME_DIMENSION } from '@/lib/analytics';
import type {
  DimensionId,
  MeasureDenomination,
  MeasureFormat,
  MeasureId,
  MeasureMeta,
  PanelMember,
  PanelResult,
  PanelRow,
} from '@/lib/analytics';
import type { ChartBucket, ChartMember, ChartValue } from '@/lib/issuingCharts';
// The same `lang` → `Intl` locale policy the date formatters use. These two
// print numbers next to those dates on one panel; they cannot be allowed to
// disagree about what locale the reader is in.
import { localeFor } from '@/lib/locale';

/**
 * Only the shape of `t` these helpers need. `defaultValue` is part of it because
 * a member can name an i18n key this build has never heard of — a sixth item
 * type ships as data long before it ships as a translation — and i18next's
 * missing-key behaviour is to return the key itself, which is not a label.
 */
type Translate = (key: string, options?: { defaultValue: string }) => string;

/**
 * One dimension of a panel, resolved: where it sits in the row keys, and who its
 * members are.
 */
export interface DimensionSlice {
  /** Position in every `rows[].key`, or -1 when the panel does not group by it. */
  readonly index: number;
  /**
   * The panel's members for this dimension, in the order the server sent them —
   * which is its stable key order, so a legend built from this needs no sorting
   * and keeps the same colour against the same category between requests.
   */
  readonly members: readonly PanelMember[];
  /** The member id this row carries for the dimension. */
  keyOf(row: PanelRow): string | undefined;
  /** Display information for a member id the panel described. */
  memberOf(id: string): PanelMember | undefined;
}

const ABSENT: DimensionSlice = {
  index: -1,
  members: [],
  keyOf: () => undefined,
  memberOf: () => undefined,
};

/**
 * Resolve a dimension against a panel.
 *
 * A panel that does not group by the dimension — or no panel at all, which is
 * every render before the request resolves — comes back as an empty slice rather
 * than as `undefined`, so callers read one shape instead of branching on
 * presence before they can ask anything.
 */
export function dimensionSlice(
  panel: PanelResult | undefined,
  dimension: DimensionId,
): DimensionSlice {
  if (!panel) return ABSENT;
  const index = panel.groupBy.indexOf(dimension);
  if (index < 0) return ABSENT;

  const members = panel.members[dimension] ?? [];
  const byId = new Map(members.map((member) => [member.id, member]));
  return {
    index,
    members,
    keyOf: (row) => row.key[index],
    memberOf: (id) => byId.get(id),
  };
}

/**
 * A member's display text. `labelKey` members are a closed enum the frontend has
 * translations for; `label` members are user data that cannot be translated —
 * the wire type makes exactly one of the two present, and this is the one place
 * that has to know which.
 */
export function memberLabel(
  t: Translate,
  member: PanelMember | undefined,
  fallback: string,
): string {
  if (!member) return fallback;
  if (member.labelKey !== undefined) return t(member.labelKey, { defaultValue: fallback });
  return member.label;
}

/**
 * One measure off one row. A measure the row is missing reads as zero: rows are
 * sparse by design (a bucket with no issuing has no row at all), so absence here
 * means "none happened", not "unknown".
 */
export function measureValue(row: PanelRow, measure: MeasureId): number {
  const value = row.values[measure];
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

/** A measure summed across rows — the whole of a panel for that measure. */
export function sumMeasure(rows: readonly PanelRow[] | undefined, measure: MeasureId): number {
  return (rows ?? []).reduce((sum, row) => sum + measureValue(row, measure), 0);
}

/**
 * The panel's own metadata for a measure, which carries its i18n key and its
 * format. Read from the payload rather than from the frontend's copy of the
 * contract so a panel that stops offering a measure stops claiming to have one.
 */
export function measureMetaOf(
  panel: PanelResult | undefined,
  measure: MeasureId,
): MeasureMeta | undefined {
  return panel?.measures.find((meta) => meta.id === measure);
}

/**
 * A measure as the page prints it.
 *
 * Switched on `MeasureFormat` with a declared return type and no `default`
 * branch, which is the precondition that type documents: the day a decimal or
 * money measure joins the union, this function stops compiling instead of
 * quietly rounding the new measure to a whole number.
 */
export function formatMeasure(value: number, format: MeasureFormat, lang: string): string {
  switch (format) {
    case 'integer':
      return new Intl.NumberFormat(localeFor(lang)).format(Math.round(value));
  }
}

/**
 * Whether summing this measure across items mixes units — which is the whole of
 * what the page needs to decide whether a figure carries the mixed-units caveat
 * and whether a per-item row prints the item's unit after its number.
 *
 * Switched on `MeasureDenomination` with a declared return type and no `default`
 * branch, for the same reason `formatMeasure` is: the caveat is a claim about
 * every measure that exists, so a new one must not be able to slip past it by
 * being neither of the two the page was written against. Widening the union
 * breaks this function first.
 */
export function mixesItemUnits(denomination: MeasureDenomination): boolean {
  switch (denomination) {
    case 'item_unit':
      return true;
    case 'count':
      return false;
  }
}

/**
 * A share, as the charts print it. Takes the percentage the geometry helpers
 * emit (0–100, one decimal place) rather than a fraction, so the rounding stays
 * in `lib/issuingCharts` and this only decides where the sign sits and which
 * digits the locale uses.
 */
export function formatShare(percent: number, lang: string): string {
  return new Intl.NumberFormat(localeFor(lang), {
    style: 'percent',
    maximumFractionDigits: 1,
  }).format(percent / 100);
}

/**
 * One measure per member of a dimension, in row order of first appearance.
 *
 * Rows sharing a member id are summed rather than the last one winning: a panel
 * grouped by more than one dimension has several rows per member of any one of
 * them, and this is what collapses the others away. That is the whole reason
 * this is a function rather than four lines at each call site — a panel that
 * gains a second grouping is a data change, and a last-row-wins loop written out
 * by hand somewhere else would answer it with one row's figure instead of the
 * member's.
 */
export function memberValues(
  panel: PanelResult | undefined,
  dimension: DimensionId,
  measure: MeasureId,
): Map<string, number> {
  const slice = dimensionSlice(panel, dimension);
  const totals = new Map<string, number>();
  if (slice.index < 0) return totals;

  for (const row of panel?.rows ?? []) {
    const id = slice.keyOf(row);
    if (id === undefined) continue;
    totals.set(id, (totals.get(id) ?? 0) + measureValue(row, measure));
  }
  return totals;
}

/**
 * A panel as `{ id, label, value }` — the vocabulary every chart primitive
 * speaks. Member order is preserved, so a server-ranked panel arrives ranked.
 *
 * A member the panel didn't describe falls back to its own id rather than to
 * blank text: the id is the least useful label that is still an honest one, and
 * an unnamed slice of a chart is worse than an ugly one.
 */
export function chartMembers(
  t: Translate,
  panel: PanelResult | undefined,
  dimension: DimensionId,
  measure: MeasureId,
): ChartMember[] {
  const slice = dimensionSlice(panel, dimension);
  if (slice.index < 0) return [];

  const totals = memberValues(panel, dimension, measure);
  const order = slice.members.length > 0 ? slice.members.map((m) => m.id) : [...totals.keys()];
  return order.map((id) => ({
    id,
    label: memberLabel(t, slice.memberOf(id), id),
    value: totals.get(id) ?? 0,
  }));
}

/**
 * The trend panel as one bucket per column, each split into segments by
 * `stackBy`.
 *
 * The axis is `members.time_bucket`: the server sends the complete, ordered set
 * of buckets the window covers — including the quiet ones — because it is the
 * side that knows where a bucket starts (a week's begins on its Monday, which
 * can precede the requested `from`). Rows stay sparse, and a bucket with no row
 * is zero, which is exactly the gap that says "this day happened and was quiet".
 *
 * A key that appears in `rows` but not in the axis is drawn rather than dropped.
 * That is not padding: it means client and server disagree about the axis, and
 * losing a bucket's issuing is a worse failure than showing an unexpected
 * column. It goes in date order rather than on the end, because a stray
 * `2026-02-23` after `2026-03-03` is a date axis that runs backwards, which
 * misreads worse than the missing column it was there to prevent.
 */
export function timeBuckets(
  panel: PanelResult | undefined,
  stackBy: DimensionId,
  measure: MeasureId,
): ChartBucket[] {
  const time = dimensionSlice(panel, TIME_DIMENSION);
  const stack = dimensionSlice(panel, stackBy);
  if (time.index < 0) return [];

  const values = new Map<string, Map<string, number>>();
  for (const row of panel?.rows ?? []) {
    const bucket = time.keyOf(row);
    if (bucket === undefined) continue;
    const segment = stack.index < 0 ? '' : (stack.keyOf(row) ?? '');
    const inBucket = values.get(bucket) ?? new Map<string, number>();
    values.set(bucket, inBucket);
    inBucket.set(segment, (inBucket.get(segment) ?? 0) + measureValue(row, measure));
  }

  const axis = time.members.map((member) => member.id);
  const known = new Set(axis);
  const extra = [...values.keys()].filter((key) => !known.has(key));
  // Bucket ids are `yyyy-mm-dd`, so a lexical sort is a chronological one, and
  // sorting an already-ordered axis leaves it alone. Only sorted when there is
  // something to place: absent a disagreement the server's order is the axis,
  // and this stays out of the way of it.
  const columns = extra.length === 0 ? axis : [...axis, ...extra].sort();
  const segmentIds =
    stack.members.length > 0
      ? stack.members.map((member) => member.id)
      : [...new Set([...values.values()].flatMap((inBucket) => [...inBucket.keys()]))];

  return columns.map((bucket) => {
    const inBucket = values.get(bucket);
    const segments: ChartValue[] = segmentIds.map((id) => ({
      id,
      value: inBucket?.get(id) ?? 0,
    }));
    // Carried from the axis member, not derived: which buckets the window only
    // partly covers is the server's answer, because the server is what put the
    // boundaries there. A bucket drawn from `extra` — one the client and server
    // disagree about — has no member to ask, and stays unmarked rather than
    // guessing.
    const partial = time.memberOf(bucket)?.partial;
    return { id: bucket, segments, ...(partial ? { partial: true } : {}) };
  });
}
